export type ModuleCategory = 'inventory' | 'safety' | 'people' | 'operations' | 'finance';

export interface ModuleConfig {
  key: string;
  name: string;
  description: string;
  icon: string; // Lucide icon name
  basePath: string;
  category: ModuleCategory;
  dependsOn?: string[];
  recommends?: string[];
}

export const MODULES: Record<string, ModuleConfig> = {
  gear: {
    key: 'inventory',
    name: 'H3Gear',
    description: 'Inventory & Asset Management',
    icon: 'Package',
    basePath: '/assets',
    category: 'inventory',
  },
  safe: {
    key: 'safety',
    name: 'H3Safe',
    description: 'Safety & Compliance',
    icon: 'ShieldCheck',
    basePath: '/safety',
    category: 'safety',
  },
  crew: {
    key: 'crew',
    name: 'H3Crew',
    description: 'Workforce & HR',
    icon: 'Users',
    basePath: '/crew',
    category: 'people',
  },
  project: {
    key: 'projects',
    name: 'H3Books',
    description: 'Billing & project finances',
    icon: 'Briefcase',
    basePath: '/projects',
    category: 'operations',
    recommends: ['crew'],
  },
  pay: {
    key: 'payroll',
    name: 'H3Pay',
    description: 'Payroll & Expenses',
    icon: 'DollarSign',
    basePath: '/payroll',
    category: 'finance',
    recommends: ['crew', 'projects'],
  },
  comply: {
    key: 'compliance',
    name: 'H3Comply',
    description: 'IRATA/SPRAT Compliance',
    icon: 'ClipboardCheck',
    basePath: '/compliance',
    category: 'safety',
    recommends: ['safety', 'crew'],
  },
  work: {
    key: 'work',
    name: 'H3Project',
    description: 'Work management — tasks/checklists under foundation Projects',
    icon: 'ListChecks',
    basePath: '/work',
    category: 'operations',
    recommends: ['projects'],
  },
};

/**
 * Branded display names for module keys.
 * Use this to replace generic labels ("safety", "crew") with branded names.
 */
export const MODULE_DISPLAY_NAMES: Record<string, string> = {
  assets: 'H3Gear',
  inventory: 'H3Gear',
  safety: 'H3Safe',
  crew: 'H3Crew',
  projects: 'H3Books',
  payroll: 'H3Pay',
  compliance: 'H3Comply',
  work: 'H3Project',
};

/** Get the branded display name for a module key, falling back to the key itself. */
export function getModuleDisplayName(key: string): string {
  return MODULE_DISPLAY_NAMES[key] ?? key;
}

/** Get a module config by its key (e.g., 'safety', 'crew') */
export function getModuleByKey(key: string): ModuleConfig | undefined {
  return Object.values(MODULES).find((m) => m.key === key);
}

/** Get all platform modules */
export function getPlatformModules(): ModuleConfig[] {
  return Object.values(MODULES);
}

/** Get recommended companion module names for a module */
export function getModuleRecommendationNames(moduleKey: string): string[] {
  const mod = getModuleByKey(moduleKey);
  if (!mod?.recommends) return [];
  return mod.recommends
    .map((recKey) => getModuleByKey(recKey)?.name)
    .filter((name): name is string => !!name);
}
